import {
  CheckSquareOutlined,
  CloseSquareOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { LabourOrder } from '@services';
import { Utils } from '@utils';
import { Button, FormInstance, Typography } from 'antd';
import React, { useState } from 'react';

const NOTE_LIMIT = 40;

const NoteCell: React.FC<{ note?: string }> = ({ note }) => {
  const [expanded, setExpanded] = useState(false);
  if (!note?.trim()) return null;
  const isLong = note.length > NOTE_LIMIT;

  return (
    <span style={{ color: '#595959', fontSize: 12 }}>
      {expanded ? note : isLong ? note.slice(0, NOTE_LIMIT) : note}
      {isLong && (
        <>
          {!expanded && '... '}
          <Typography.Link
            style={{ fontSize: 11 }}
            onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          >
            {expanded ? ' less' : 'more'}
          </Typography.Link>
        </>
      )}
    </span>
  );
};

interface IProps {
  form: FormInstance<any>;
  hideAction: boolean;
  editingKey: string;
  showDelete: boolean;
  handleDeleteClick: (id: string, serviceOrderId?: string) => void;
  isEditing: (record: LabourOrder) => boolean;
  handleCancel: () => void;
  handleUpdate: (key: React.Key) => void;
  handleUpdateExtra: (key: React.Key, parentKey?: React.Key) => void;
  handleEdit: (
    record: Partial<LabourOrder> & {
      key: React.Key;
    }
  ) => void;
}

export const LabourColumns = ({
  form,
  hideAction,
  editingKey,
  showDelete,
  isEditing,
  handleCancel,
  handleUpdate,
  handleUpdateExtra,
  handleEdit,
  handleDeleteClick,
}: IProps) => {
  const columns = [
    {
      title: 'Name',
      key: 'title',
      dataIndex: 'title',
      width: 250,
      render: (title: string) => (
        <div style={{ whiteSpace: 'pre-line', paddingLeft: '25px' }}>{title}</div>
      ),
    },
    {
      title: 'Note',
      key: 'note',
      dataIndex: 'note',
      width: 250,
      render: (_: any, record: LabourOrder) =>
        record.isExtra ? null : <NoteCell note={(record as any).note} />,
    },
    {
      title: 'Type',
      key: 'type',
      dataIndex: 'type',
      editable: true,
      required: true,
      align: 'right' as const,
      render: (type: string) => <div style={{ whiteSpace: 'pre-line' }}>{type}</div>,
    },
    {
      title: 'Cost price',
      dataIndex: 'costPrice',
      key: 'costPrice',
      editable: true,
      inputType: 'number',
      align: 'right' as const,
      width: 100,
      render: (costPrice: number) => Utils.formatCurrency(costPrice),
    },
    {
      title: 'Margin',
      dataIndex: 'margin',
      key: 'margin',
      editable: true,
      inputType: 'number',
      align: 'right' as const,
      width: 100,
      render: (margin: number) =>
        margin !== undefined && margin !== null ? `${Utils.formatNumberOrPercentage(margin)}%` : '',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      editable: true,
      required: true,
      inputType: 'number',
      align: 'right' as const,
      width: 100,
      render: (price: number) => Utils.formatCurrency(price),
    },
    {
      title: 'Quantity',
      key: 'timeSpent',
      dataIndex: 'timeSpent',
      editable: true,
      required: true,
      inputType: 'number',
      align: 'right' as const,
      width: 100,
      render: (hours: number) => Utils.formatNumberOrPercentage(hours),
    },
    {
      title: 'Discount',
      dataIndex: 'discount',
      key: 'discount',
      editable: true,
      required: true,
      inputType: 'number',
      align: 'right' as const,
      width: 60,
      render: (discount: number) => `${Utils.formatNumberOrPercentage(discount)}%`,
    },
    {
      title: 'VAT',
      dataIndex: 'vat',
      key: 'vat',
      editable: true,
      inputType: 'number',
      align: 'right' as const,
      width: 33,
      render: (vat: number) => `${Utils.formatNumberOrPercentage(vat)}%`,
    },
    {
      title: 'Total',
      key: 'total',
      dataIndex: 'total',
      editable: true,
      align: 'right' as const,
      width: 110,
      render: (total: string) => Utils.formatCurrency(parseFloat(total)),
    },
    {
      title: 'Action',
      width: 100,
      align: 'right' as const,
      hidden: hideAction,
      render: (_: any, record: LabourOrder) => {
        const editable = isEditing(record);
        return (
          (editable && (
            <>
              <CheckSquareOutlined
                onClick={() => {
                  if (record.isExtra) {
                    handleUpdateExtra(record.id, record.serviceOrderId);
                  } else {
                    handleUpdate(record.id);
                  }
                }}
                style={{ marginRight: 8, fontSize: 20 }}
              />
              <CloseSquareOutlined style={{ fontSize: 20 }} onClick={handleCancel} />
            </>
          )) || (
            <>
              <Button
                icon={<EditOutlined style={{ fontSize: 15 }} />}
                type="text"
                disabled={editingKey !== ''}
                onClick={() => handleEdit({ ...record, key: record.id })}
              />
              {record.isExtra ? (
                <Button
                  type="text"
                  icon={<DeleteOutlined style={{ fontSize: 15 }} />}
                  onClick={() => handleDeleteClick(record.id, record.serviceOrderId)}
                />
              ) : (
                showDelete && (
                  <Button
                    type="text"
                    icon={<DeleteOutlined style={{ fontSize: 15 }} />}
                    onClick={() => handleDeleteClick(record.id)}
                  />
                )
              )}
            </>
          )
        );
      },
    },
  ];
  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: LabourOrder) => ({
        ...col,
        record,
        form,
        editing: isEditing(record),
        isExtra: record.isExtra,
      }),
    };
  });

  return mergedColumns.filter((x) => !x.hidden);
};
